import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Globe, Check } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function MultiCurrencyFeature() {
  useSEO({
    title: "Multi-currency Wallet",
    description: "Hold EUR, USD, GBP, RON and more in a single wallet. Pay any merchant in any currency, settle to yours.",
    path: "/patrons/multi-currency",
  });
  return (
    <main data-testid="patrons-multi-currency" className="bg-bg text-white min-h-screen">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <Link to="/" data-testid="patrons-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-fg hover:text-gold transition-colors">
          <ArrowLeft size={14} /> Back to the Landing
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 03 / For Patrons · Multi-currency</div>
            <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              One wallet,<br/><span className="italic gold-text">every currency.</span>
            </h1>
            <p className="mt-8 text-secondary-fg text-lg leading-relaxed max-w-xl font-light">
              Hold balances in EUR, USD, GBP, RON and more inside a single y ℶα wallet. Pay any merchant in their currency — we settle in yours. Switch with a glance; convert when it suits you.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/register" className="btn-gold inline-flex items-center gap-2">Open a wallet <ArrowUpRight size={16} /></Link>
              <Link to="/login" className="btn-ghost">Sign in</Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="glass p-8 rounded-md">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-fg mb-3">Sample wallet</div>
              <div className="space-y-3">
                {[
                  ["EUR", "€ 12,480.00"],
                  ["USD", "$ 8,210.50"],
                  ["GBP", "£ 4,300.00"],
                  ["RON", "lei 14,200.00"],
                ].map(([k,v]) => (
                  <div key={k} className="flex justify-between items-center py-2 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <Globe size={14} className="text-gold"/>
                      <span className="text-sm tracking-widest">{k}</span>
                    </div>
                    <span className="font-mono text-sm">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border hairline-strong">
          {[
            ["Hold many, pay any", "Spend in EUR, USD, GBP, RON — pay any merchant in the currency they price in."],
            ["Real-time rates", "Mid-market FX with a transparent margin. No hidden spread, no double-conversion."],
            ["Cross-currency P2P", "Send EUR; the recipient receives USD. The math happens silently."],
            ["Discreet conversion", "Convert between currencies in the wallet whenever it suits you."],
            ["Per-currency ledger", "Each currency has its own audit trail and reconciliation."],
            ["Stripe payouts", "Withdraw to any rail Stripe supports in the currency of your choice."],
          ].map(([t, b]) => (
            <div key={t} className="bg-bg p-8">
              <div className="flex items-start gap-3">
                <Check className="text-gold mt-1 shrink-0" size={18} strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl mb-2">{t}</h3>
                  <p className="text-secondary-fg text-sm leading-relaxed font-light">{b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
            Currency, <span className="italic gold-text">silenced.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/register" className="btn-gold inline-flex items-center gap-2">Begin your wallet <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
